import _ from 'lodash';

import { Injectable } from '@angular/core';
import { lastValueFrom, Observable, BehaviorSubject, Subscription } from 'rxjs';

import { State } from 'src/app/shared/models/state';
import { map } from 'rxjs/operators';
import { Race } from 'src/app/shared/models/race';
import { Country } from 'src/app/shared/models/country';
import { DemographicInfo } from 'src/app/people/models/demographicInfo';
import { IAddressModel } from 'src/app/people/models/addressModel';
import { TimeZoneModel } from '../../app/shared/models/timeZoneModel';
import { PortalBaseService } from 'src/portal/portal-base.service';
import { PersonApplicationWrite } from 'src/app/people/models/personApplicationWrite.model';

@Injectable({ providedIn: 'root' })
export class LookupService extends PortalBaseService {
  private basePath = 'lookup';
  private racesPath = 'races';
  private statesPath = 'states';
  private countriesPath = 'countries';
  private timeZonesPath = 'timezones';
  private _states: State[] = [];
  private _countries: Country[] = [];
  public readonly races: BehaviorSubject<Race[]> = new BehaviorSubject<Race[]>(null);
  public readonly states: BehaviorSubject<State[]> = new BehaviorSubject<State[]>(null);
  public readonly countries: BehaviorSubject<Country[]> = new BehaviorSubject<Country[]>(null);

  constructor() {
    super();
    this.getRaces();
    this.getStates();
    this.getCountries();
  }

  getRaces(): Observable<Race[]> {
    let obs = this.get(this.getPath(this.racesPath)).pipe(map(this.convertRaces));

    this.updateRaces(obs);

    return obs;
  }

  getStates(): Promise<State[]> {
    let obs = this.get(this.getPath(this.statesPath)).pipe(map(this.convertStates));

    this.updateStates(obs);

    return lastValueFrom(obs);
  }

  getCountries(): Promise<Country[]> {
    let obs = this.get(this.getPath(this.countriesPath)).pipe(map(this.convertCountries));

    this.updateCountries(obs);

    return lastValueFrom(obs);
  }

  getTimeZones(): Observable<TimeZoneModel[]> {
    return this.get(this.getPath(this.timeZonesPath)).pipe(map(this.convertTimeZones));
  }

  updateRaces(obs) {
    obs.subscribe((data) => {
      this.races.next(data);
    });
  }

  updateStates(obs) {
    obs.subscribe((data) => {
      this._states = data;
      this.states.next(data);
    });
  }

  updateCountries(obs) {
    obs.subscribe((data) => {
      this._countries = data;
      this.countries.next(data);
    });
  }

  //a helper service which is guaranteed to have state, province, and country arrays setup. Also have a few helper functions
  public getStateCountryLookup(): Promise<StateCountryLookupService> {
    if (this._stateCountryLookupService) {
      return Promise.resolve(this._stateCountryLookupService);
    }
    if (this._states.length && this._countries.length) {
      return Promise.resolve(this.buildStateCountryLookupService(this._states, this._countries));
    }

    let statesSubscription: Subscription;
    let countriesSubscription: Subscription;

    return new Promise<{ states: State[]; countries: Country[] }>((resolve) => {
      let states = [];
      let countries = [];

      statesSubscription = this.states.subscribe((statesApi) => {
        //lookupService.states is initialized as new BehaviorSubject<State[]>(null)
        //so on F5 sometimes states is null. Maybe should be "BehaviorSubject<State[]>([])"?
        if (!statesApi?.length) return;
        states = statesApi;
        if (countries.length) resolve({ states, countries });
      });
      countriesSubscription = this.countries.subscribe((countriesApi) => {
        if (!countriesApi?.length) return;
        countries = countriesApi;
        if (states.length) resolve({ states, countries });
      });
    }).then(({ states, countries }) => {
      countriesSubscription.unsubscribe();
      statesSubscription.unsubscribe();
      return this.buildStateCountryLookupService(states, countries);
    });
  }

  private _stateCountryLookupService: StateCountryLookupService;
  private buildStateCountryLookupService(states: State[], countries: Country[]) {
    const usStates = states.filter((state) => state.country.code === Country.UnitedStates.code);
    const provinces = states.filter((state) => state.country.code === Country.Canada.code);
    this._stateCountryLookupService = new StateCountryLookupService(countries, usStates, provinces);
    return this._stateCountryLookupService;
  }

  private getPath(path) {
    return `v1/${this.basePath}/${path}`;
  }

  private convertRaces(races: Race[]): Race[] {
    return _.each(races, function (race) {
      return Race.parse(race);
    });
  }

  private convertStates(states: State[]): State[] {
    return _.each(states, function (state) {
      return State.parse(state);
    });
  }

  private convertCountries(countries: Country[]): Country[] {
    return _.each(countries, function (country) {
      return Country.parse(country);
    });
  }

  private convertTimeZones(timeZones: TimeZoneModel[]): TimeZoneModel[] {
    return _.each(timeZones, function (timeZone) {
      return TimeZoneModel.parse(timeZone);
    });
  }
}

export class StateCountryLookupService {
  constructor(
    public countries: Country[],
    public states: State[],
    public provinces: State[]
  ) {}

  //** builds the DemographicInfo from IAddressModel. Does not set races/gender */
  buildDemographicInfo = (model: IAddressModel): DemographicInfo =>
    DemographicInfo.Build(
      model,
      this.states.find((s) => s.id === model.stateId) ??
        this.provinces.find((s) => s.id === model.stateId),
      this.countries.find((c) => c.id === (model.countryId ?? Country.UnitedStates.id))
    );

  //** builds the DemographicInfo with races and gender */
  buildDemographicInfoFromPersonApplication = (model: PersonApplicationWrite): DemographicInfo => {
    const demographicInfo = DemographicInfo.Build(
      model,
      this.states.find((s) => s.id === model.stateId) ??
        this.provinces.find((s) => s.id === model.stateId),
      this.countries.find((c) => c.id === (model.countryId ?? Country.UnitedStates.id))
    );
    demographicInfo.races = model.races;
    demographicInfo.gender = model.gender;
    return demographicInfo;
  };

  getStateByCode = (code: string): State => {
    if (code) {
      return (
        this.states.find((s) => s.code === code) ??
        this.provinces.find((province) => province.code === code)
      );
    }
  };
}
